import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { sortByEndDateDesc } from "../../../utils/sortByDate";
import { HiLink, HiBadgeCheck } from "react-icons/hi";
import Tooltip from '../../shared/Tooltip';
import { RichTextContent } from '../../../utils/richTextProcessor';
import { createProfileViewRequestAction } from '../../../redux/actions/userActions';
import { getSentProfileViewRequestsAction } from '../../../redux/actions/profileViewRequestActions';
import { useToast } from '../../../contexts/ToastContext';

const LearningsTab = ({
  selectedLearning,
  setSelectedLearning,
  isEducationExpanded,
  setIsEducationExpanded,
  isCertificationsExpanded,
  setIsCertificationsExpanded,
  isTrainingsExpanded,
  setIsTrainingsExpanded,
  isLearningCardExpanded,
  setIsLearningCardExpanded,
  isDescriptionVisible,
  setIsDescriptionVisible,
  setVerificationOverlay,
  learningsData, // New prop for transformed data
  isPrivateProfile = false,
  profileUserId = null,
  isAuthenticated = true,
  onRequireAuth = null,
  isViewingOwnProfile = false,
}) => {
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();
  const [requestLoading, setRequestLoading] = useState(false);
  
  // Get sent profile view requests from Redux
  const sentProfileViewRequests = useSelector((state) => state.user?.sentProfileViewRequests || []);

  // Fetch sent requests on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && profileUserId && !isViewingOwnProfile) {
      dispatch(getSentProfileViewRequestsAction(null, 25, 0));
    }
  }, [dispatch, isAuthenticated, profileUserId, isViewingOwnProfile]);

  // Find existing profile view request for this profile owner
  const existingProfileViewRequest = useMemo(() => {
    if (!profileUserId || !sentProfileViewRequests || sentProfileViewRequests.length === 0 || isViewingOwnProfile) {
      return null;
    }
    
    const found = sentProfileViewRequests.find((request) => {
      const profileOwnerId = request.profileOwner?._id || request.profileOwner;
      return profileOwnerId && String(profileOwnerId) === String(profileUserId) && request.status === "pending";
    });
    
    return found;
  }, [sentProfileViewRequests, profileUserId, isViewingOwnProfile]);

  const handleRequestAccess = async () => {
    if (!isAuthenticated) {
      if (onRequireAuth) {
        onRequireAuth("auth_required");
      }
      return;
    }

    if (!profileUserId || isViewingOwnProfile) return;

    setRequestLoading(true);
    try {
      const result = await dispatch(createProfileViewRequestAction(profileUserId));
      if (result?.success) {
        showSuccess("Profile view request sent successfully");
        // Refresh sent requests to update button state
        await dispatch(getSentProfileViewRequestsAction(null, 25, 0));
      } else {
        showError(result?.error || "Failed to send request");
      }
    } catch (error) {
      showError("Error sending request");
    } finally {
      setRequestLoading(false);
    }
  };
  // Create a dynamic accordion state management
  const [accordionStates, setAccordionStates] = React.useState({});
  
  // State for expandable cards
  const [expandedCards, setExpandedCards] = React.useState({});

  // Function to toggle accordion state - only one can be open at a time
  const toggleAccordion = (category) => {
    setAccordionStates(prev => {
      const newState = {};
      // Close all accordions first
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      // Open only the clicked one if it wasn't already open
      const isOpening = !prev[category];
      newState[category] = isOpening;
      
      // If opening a section, select the first card but don't expand it
      if (isOpening) {
        let firstCardId = null;
        if (category === 'Education' && educationList.length > 0) {
          firstCardId = educationList[0].id;
        } else if (category === 'Certifications' && certificationsList.length > 0) {
          firstCardId = certificationsList[0].id;
        } else if (category === 'Trainings' && trainingsList.length > 0) {
          firstCardId = trainingsList[0].id;
        }
        
        if (firstCardId) {
          setSelectedLearning(firstCardId);
          // Start with all cards collapsed
          setExpandedCards({});
        }
      }
      
      return newState;
    });
  };

  // Function to get accordion state
  const getAccordionState = (category) => {
    return accordionStates[category] || false;
  };

  // Function to toggle card expansion - only one card can be open at a time within the same section
  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => {
      const newState = { ...prev };
      
      // Find which section this card belongs to
      let currentSection = null;
      if (educationList.some(edu => edu.id === cardId)) {
        currentSection = 'Education';
      } else if (certificationsList.some(cert => cert.id === cardId)) {
        currentSection = 'Certifications';
      } else if (trainingsList.some(training => training.id === cardId)) {
        currentSection = 'Trainings';
      }
      
      // Close all cards in the current section first
      if (currentSection === 'Education') {
        educationList.forEach(edu => {
          newState[edu.id] = false;
        });
      } else if (currentSection === 'Certifications') {
        certificationsList.forEach(cert => {
          newState[cert.id] = false;
        });
      } else if (currentSection === 'Trainings') {
        trainingsList.forEach(training => {
          newState[training.id] = false;
        });
      }
      
      // Open only the clicked card if it wasn't already open
      const isOpening = !prev[cardId];
      newState[cardId] = isOpening;
      return newState;
    });
  };

  // Function to get card expansion state
  const isCardExpanded = (cardId) => {
    return expandedCards[cardId] || false;
  };

  // Get learnings data from API only - no hardcoded fallback
  const educationList = React.useMemo(() => sortByEndDateDesc(learningsData?.education || []), [learningsData?.education]);
  const certificationsList = React.useMemo(() => sortByEndDateDesc(learningsData?.certifications || []), [learningsData?.certifications]);
  const trainingsList = React.useMemo(() => sortByEndDateDesc(learningsData?.trainings || []), [learningsData?.trainings]);

  // Initialize accordion states when learnings data changes
  React.useEffect(() => {
    const initialStates = {};
    if (educationList.length > 0) {
      initialStates['Education'] = true; // Open first section by default
    }
    if (certificationsList.length > 0) {
      initialStates['Certifications'] = false;
    }
    if (trainingsList.length > 0) {
      initialStates['Trainings'] = false;
    }
    if (Object.keys(initialStates).length > 0) {
      setAccordionStates(initialStates);
    }

    // Select first card but don't expand it by default
    if (educationList.length > 0) {
      const firstEducationId = educationList[0].id;
      setSelectedLearning(firstEducationId);
      setExpandedCards({});
    }
  }, [educationList, certificationsList, trainingsList, setSelectedLearning]);

  if (isPrivateProfile) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        {/* Left - Learnings list area */}
        <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex flex-col items-center justify-center text-center">
          <svg
            className="w-8 h-8 text-amber-500 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading mb-1">
            Private account
          </h3>
          <p className="text-xs md:text-sm text-gray-600 font-roboto-flex mb-3">
            This is a private account.
          </p>
          {!isViewingOwnProfile && (
            <button
              onClick={handleRequestAccess}
              disabled={requestLoading || existingProfileViewRequest}
              className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                existingProfileViewRequest
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : "bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
            >
              {requestLoading ? (
                "Sending..."
              ) : existingProfileViewRequest ? (
                "Request Pending"
              ) : (
                "Request MYST Access"
              )}
            </button>
          )}
        </div>

        {/* Right - Learnings details */}
        <div className="col-span-1 md:col-span-3 bg-white rounded-lg border border-gray-200 shadow-sm p-8 flex flex-col items-center justify-center text-center">
          <svg
            className="w-10 h-10 text-amber-500 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 font-heading mb-2">
            Private account
          </h2>
          <p className="text-sm text-gray-600 font-roboto-flex max-w-md">
            This is a private account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
      {/* Left Card - Learnings List */}
      <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-3 md:p-3 accordion-section">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading">
            Learnings
          </h3>
          {isPrivateProfile && (
            <svg
              className="w-4 h-4 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
              />
            </svg>
          )}
        </div>
        
        {/* Education Section */}
        {educationList.length > 0 && (
        <div className="mb-2 accordion-list">
          <button
              onClick={() => toggleAccordion('Education')}
            className={`accordion-button w-full rounded-md text-xs md:text-sm font-bold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                getAccordionState('Education') ? 'expanded' : ''
            }`}
              aria-expanded={getAccordionState('Education')}
          >
            <span className="accordion-header">Education</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
            {getAccordionState('Education') && (
            <div className="space-y-2 mt-2">
                {educationList.map((education) => (
                <button
                    key={education.id}
                    onClick={() => {
                      setSelectedLearning(education.id);
                      toggleCardExpansion(education.id);
                    }}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                      selectedLearning === education.id
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                    data-tooltip={education.title}
                    title={education.title}
                >
                    {education.title}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Certifications Section */}
        {certificationsList.length > 0 && (
        <div className="mb-2 accordion-list">
          <button
              onClick={() => toggleAccordion('Certifications')}
              className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                getAccordionState('Certifications') ? 'expanded' : ''
              }`}
              aria-expanded={getAccordionState('Certifications')}
          >
            <span className="accordion-header">Certifications</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
            {getAccordionState('Certifications') && (
            <div className="space-y-2 mt-2">
                {certificationsList.map((certification) => (
                <button
                    key={certification.id}
                    onClick={() => {
                      setSelectedLearning(certification.id);
                      toggleCardExpansion(certification.id);
                    }}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                      selectedLearning === certification.id
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                    data-tooltip={certification.title}
                    title={certification.title}
                >
                    {certification.title}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Trainings Section */}
        {trainingsList.length > 0 && (
          <div className="mt-2 accordion-list">
          <button
              onClick={() => toggleAccordion('Trainings')}
              className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                getAccordionState('Trainings') ? 'expanded' : ''
              }`}
              aria-expanded={getAccordionState('Trainings')}
          >
            <span className="accordion-header">Trainings</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
            {getAccordionState('Trainings') && (
            <div className="space-y-2 mt-2">
                {trainingsList.map((training) => (
                <button
                    key={training.id}
                    onClick={() => {
                      setSelectedLearning(training.id);
                      toggleCardExpansion(training.id);
                    }}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                      selectedLearning === training.id
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                    data-tooltip={training.title}
                    title={training.title}
                >
                    {training.title}
                </button>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
      
      {/* Right Card - Learning Details */}
      <div className="col-span-1 md:col-span-3 space-y-3">
        {/* Education Cards - Only show when Education accordion is expanded */}
        {getAccordionState('Education') && educationList.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <h4 className="font-semibold text-gray-800 mb-3 font-heading text-base">Education</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {educationList.map((education, index) => {
                const isExpanded = isCardExpanded(education.id);
                const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                const shouldShow = !hasAnyExpanded || isExpanded;
                
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={education.id}
                    className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                      isExpanded ? 'col-span-2' : ''
                    }`}
                    onClick={() => {
                      setSelectedLearning(education.id);
                      toggleCardExpansion(education.id);
                    }}
                  >
                    <div className={`${isExpanded ? 'p-4' : 'p-3'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isExpanded ? 'text-base' : 'text-sm'}`}>
                          {education.title}
                        </h5>
                        <div className="flex items-center gap-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCards({});
                                setVerificationOverlay({ show: false });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {isExpanded && Array.isArray(education.validationData) && education.validationData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVerificationOverlay({
                                  show: true,
                                  title: education.title,
                                  validationData: education.validationData || []
                                });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            </button>
                          )}
                          {isExpanded && education.attachmentExists && (
                            <Tooltip text="Attachment available on MYST">
                              <HiLink className="w-4 h-4 text-gray-600" />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      <p className="font-flex-regular text-gray-600 text-xs mb-1">{education.institute}</p>
                      <p className="font-flex-regular text-gray-500 text-xs mb-2">{education.duration}</p>
                      
                      {isExpanded && (
                        <div className="mt-3 space-y-2">
                          {education.description && (
                  <RichTextContent 
                    content={education.description}
                    className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                  />
                          )}
                          {education.cgpa && (
                            <RichTextContent 
                              content={`Grade/CGPA: ${education.cgpa}`}
                              className="font-flex-regular text-sm text-gray-700 font-medium"
                            />
                          )}
                          {education.kpi && (
                  <RichTextContent 
                    content={`KPI: ${education.kpi}`}
                    className="font-flex-regular text-sm text-gray-700 font-medium"
                  />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Certifications Cards - Only show when Certifications accordion is expanded */}
        {getAccordionState('Certifications') && certificationsList.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <h4 className="font-semibold text-gray-800 mb-3 font-heading text-base">Certifications</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {certificationsList.map((certification, index) => {
                const isExpanded = isCardExpanded(certification.id);
                const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                const shouldShow = !hasAnyExpanded || isExpanded;
                
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={certification.id}
                    className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                      isExpanded ? 'col-span-2' : ''
                    }`}
                    onClick={() => {
                      setSelectedLearning(certification.id);
                      toggleCardExpansion(certification.id);
                    }}
                  >
                    <div className={`${isExpanded ? 'p-4' : 'p-3'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isExpanded ? 'text-base' : 'text-sm'}`}>
                          {certification.title}
                        </h5>
                        <div className="flex items-center gap-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCards({});
                                setVerificationOverlay({ show: false });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {isExpanded && Array.isArray(certification.validationData) && certification.validationData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVerificationOverlay({
                                  show: true,
                                  title: certification.title,
                                  validationData: certification.validationData || []
                                });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            </button>
                          )}
                          {isExpanded && certification.attachmentExists && (
                            <Tooltip text="Attachment available on MYST">
                              <HiLink className="w-4 h-4 text-gray-600" />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      <p className="font-flex-regular text-gray-600 text-xs mb-1">{certification.organization}</p>
                      <p className="font-flex-regular text-gray-500 text-xs mb-2">{certification.duration}</p>
                      
                      {isExpanded && (
                        <div className="mt-3 space-y-2">
                          {certification.description && (
                  <RichTextContent 
                    content={certification.description}
                    className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                  />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Trainings Cards - Only show when Trainings accordion is expanded */}
        {getAccordionState('Trainings') && trainingsList.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <h4 className="font-semibold text-gray-800 mb-3 font-heading text-base">Trainings</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {trainingsList.map((training, index) => {
                const isExpanded = isCardExpanded(training.id);
                const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                const shouldShow = !hasAnyExpanded || isExpanded;
                
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={training.id}
                    className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                      isExpanded ? 'col-span-2' : ''
                    }`}
                    onClick={() => {
                      setSelectedLearning(training.id);
                      toggleCardExpansion(training.id);
                    }}
                  >
                    <div className={`${isExpanded ? 'p-4' : 'p-3'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isExpanded ? 'text-base' : 'text-sm'}`}>
                          {training.title}
                        </h5>
                        <div className="flex items-center gap-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCards({});
                                setVerificationOverlay({ show: false });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {isExpanded && Array.isArray(training.validationData) && training.validationData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVerificationOverlay({
                                  show: true,
                                  title: training.title,
                                  validationData: training.validationData || []
                                });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            </button>
                          )}
                          {isExpanded && training.attachmentExists && (
                            <Tooltip text="Attachment available on MYST">
                              <HiLink className="w-4 h-4 text-gray-600" />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      <p className="font-flex-regular text-gray-600 text-xs mb-1">{training.organization}</p>
                      <p className="font-flex-regular text-gray-500 text-xs mb-2">{training.duration}</p>
                      
                      {isExpanded && (
                        <div className="mt-3 space-y-2">
                          {training.description && (
                  <RichTextContent 
                    content={training.description}
                    className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                  />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Show message if no accordion is expanded or no learnings data */}
        {!getAccordionState('Education') && !getAccordionState('Certifications') && !getAccordionState('Trainings') && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <div className="text-center text-gray-500 py-8">
              <p className="text-sm font-medium font-roboto-flex">Select a category from the left to view details</p>
            </div>
          </div>
        )}

        {/* Show message if accordion is expanded but no data */}
        {(getAccordionState('Education') && educationList.length === 0) ||
         (getAccordionState('Certifications') && certificationsList.length === 0) ||
         (getAccordionState('Trainings') && trainingsList.length === 0) ? (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <div className="text-center text-gray-500 py-8">
              <p className="text-sm font-medium font-roboto-flex">No data available for this category</p>
        </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LearningsTab;
